import { 
  Component, 
  computed, 
  DestroyRef, 
  inject, 
  OnInit, 
  signal 
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Router, RouterLink } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { 
  catchError,
  distinctUntilChanged, 
  finalize, 
  of,
  switchMap,
  take
} from 'rxjs';

import { AuthService } from '@services/auth/authService.service';
import { UserBehaviorService } from '@services/user-events/user-behavior.service';
import { AlertService } from '@services/adaptors/alert.service';

import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { ResponseData, formatNotificationDate } from '@shared/helpers';

import { LocalStorageService } from 'src/app/connectors/localstorage';
import { MenuStateService } from 'src/app/home/core/services/menu-state.service';
import { MenuService } from 'src/app/home/core/services/menu.service';

import { NotificationsService } from '@core/notifications/services/notifications.service';
import { Notification, SourceSystemEnum } from '@core/notifications/models';

import { MenuItem } from '../../models';

@Component({
  selector: 'lib-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    RouterLink,
    AvatarComponent
  ]
})
export class HeaderComponent implements OnInit {
  readonly menuStateService = inject(MenuStateService);
  #alertService = inject(AlertService);
  #authService = inject(AuthService);
  #menuStateService = inject(MenuStateService);
  #menuService = inject(MenuService);
  #localStorageService = inject(LocalStorageService);
  #userBehavior = inject(UserBehaviorService);
  #notificationService = inject(NotificationsService);
  #destroyRef = inject(DestroyRef);
  #router = inject(Router);
  
  protected readonly SourceSystemEnum = SourceSystemEnum;
  
  menuData = signal<ResponseData<MenuItem[]>>({
    data: null,
    status: 'loading'
  });
  headerMenuItems = signal<MenuItem[]>([
    {
      title: 'My Profile',
      icon: 'fa-solid fa-circle-user',
      link: '/my-workspace/my-profile'
    },
    {
      title: 'Notifications',
      icon: 'fa-solid fa-bell',
      link: '/my-workspace/notifications-center'
    },
    // {
    //   title: 'Subscribes',
    //   icon: 'fa-solid fa-bookmark',
    //   link: '/my-workspace/subscribes'
    // },
    // {
    //   title: 'Favorites',
    //   icon: 'fa-solid fa-star',
    //   link: '/my-workspace/favorites'
    // },
    {
      title: 'Service Center',
      icon: 'fa-solid fa-list-check',
      link: '/my-workspace/service-center/homepage'
    },
  ])

  userInfo = toSignal(
    this.#userBehavior.selectedUser.pipe(distinctUntilChanged()), 
    { initialValue: { name: '', email: '', from: '' } }
  );
  readonly #userId = Number(this.#localStorageService.decryptAndGet('userId'));

  openTasks = this.#notificationService.openTasksCount;
  serviceCenterIcon = computed(() => {
    const notificationsCount = this.openTasks();
    return notificationsCount > 0 ? 
      'assets/images/icons/homepage/header/list_check_badge.svg' : 
      'assets/images/icons/homepage/header/list_check.svg';
  })

  unreadNotificationsCount = this.#notificationService.unreadNotificationsCount;
  notifications = this.#notificationService.headerNotifications;

  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });

  
  ngOnInit(): void {
    this.#listenerUserBehavior();
  }

  #listenerUserBehavior(): void {
    this.#userBehavior.selectedUser
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((user) => {
        if (user.name && user.email) {
          this.#fetchMenuItems(user.email);
        }
      });
  }

  #fetchMenuItems(email: string): void {
    this.#menuService
      .getMenusWithAccess(email)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#menuStateService.setMenuItems(this.menuData());
        })
      )
      .subscribe({
        next: (res) => {
          const processedData = this.#processMenuItems(res);
          this.menuData.set({
            data: processedData,
            status: 'success'
          });
        },
        error: () => {
          this.menuData.set({
            data: null,
            status: 'error'
          });
        }
      });
  }

  #processMenuItems(menuItems: MenuItem[]): MenuItem[] {
    const filteredItems = menuItems.filter(item => item.access);
    return filteredItems.map((item) => {
      const newItem = { ...item };
      if (newItem.items && newItem.items.length > 0) newItem.isExpanded = false;
      if (newItem.access === null) newItem.access = false;
      if (newItem.items) newItem.items = this.#processMenuItems(newItem.items);
      return newItem;
    });
  }


  shouldShowDivisor(currentIndex: number): boolean {
    const notifications = this.notifications();
    if (currentIndex === 0) return false;
    
    const currentNotification = notifications[currentIndex];
    const previousNotification = notifications[currentIndex - 1];
    
    /* Task Management !== SCOUT (Conversation/Sales Order) */
    const currentIsTaskManagement = currentNotification.documentSource === 'Task Management';
    const previousIsTaskManagement = previousNotification.documentSource === 'Task Management';
    
    return currentIsTaskManagement !== previousIsTaskManagement;
  }

  processNotificationMessage(message: string): string {
    const idPattern = /#\d+/g;
    const processedStatus = message.replace(/'([^']*)'/g, '<span class="notification-status">$1</span>');
    const finalMessage = processedStatus.replace(idPattern, '<b class="task-id">$&</b>');
    return finalMessage;
  }

  formatDate(dateString: string): string {
    return formatNotificationDate(dateString);
  }

  onTaskIdClick(event: Event, notification: Notification): void {
    const target = event.target as HTMLElement;
      if (target.classList.contains('task-id')) {
        event.stopPropagation(); 
        this.redirectTo(notification.path, notification.notificationRecipientsId);
    }
  }

  markAllAsRead(): void {
    this.#notificationService.markAllAsRead(this.#userId)
      .subscribe({
        next: () => this.#alertService.showDefaultSuccessMessage(),
        error: () => this.#alertService.showDefaultErrorMessage()
      });
  }

  markSingleAsRead(notificationId: string): void {
    this.#notificationService
      .markAsRead(notificationId)
      .pipe(
        switchMap(() => {
          return this.#notificationService.getHeaderNotifications({
            userId: this.#userId,
            limit: 5
          });
        }),
        take(1),
        catchError((error) => {
          console.error('Error marking notification as read on redirect:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  redirectTo(route: string, notificationId?: string): void {
    if (notificationId) {
      this.markSingleAsRead(notificationId);
    }

    const url = this.#router.serializeUrl(
      this.#router.createUrlTree([route])
    );
    window.open(url, '_blank');
  }

  logout(): void {
    this.#authService.logout('manual')
  }
}